import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTopics, setCurrentTopic } from '../../slices/topicsSlice';
import { updatePoints } from '../../slices/authSlice';
import { useNavigate } from 'react-router-dom';
import { styles } from './TopicStyles';
import axios from 'axios';
import TutorialModal from './TutorialModal';



const Topics = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { list: topics } = useSelector(state => state.topics);

  const { user, isGuest } = useSelector(state => state.auth);
  const [hoveredId, setHoveredId] = useState(null);
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    dispatch(fetchTopics());
    /*
    const hasSeen = localStorage.getItem('hasSeenTutorial');
    if (!hasSeen) {
      setShowTutorial(true);
    }
    */
  }, [dispatch]);

  const closeTutorial = () => {
    // localStorage.setItem('hasSeenTutorial', 'true');
    setShowTutorial(false);
  };

  // 🚩 [추가] 인앱 브라우저 및 저사양 모바일 성능을 위한 리스트 메모리화
  // 호버(onMouseEnter) 시마다 제목을 자르는 연산이 반복되어 버벅이는 현상을 원천 차단합니다.
  const memoizedTopics = useMemo(() => {
    return topics.map(topic => ({
      ...topic,
      mainTitle: topic.title.split('\n')[0] // 렌더링 전 미리 계산
    }));
  }, [topics]);

  const isAdmin = user?.role === 'ADMIN';

  // 🚩 [추가] 전장 입장 및 포인트 차감 로직
  const handleEntry = async (topic) => {

    // 1. 게스트라면 아무 묻지도 따지지도 않고 바로 입장
    if (isGuest) {
      dispatch(setCurrentTopic(topic));
      navigate(`/topics/${topic._id}`);
      return; // 함수 종료
    }

    // 1. 이미 참여 중인 전장인지 확인
    const isJoined = user?.joinedTopics?.includes(topic._id);

    if (isJoined || isAdmin) {
      // 이미 참여 중이면 바로 입장
      dispatch(setCurrentTopic(topic));
      navigate(`/topics/${topic._id}`);
    } else {
      // 2. 처음 입장하는 전장일 경우 포인트 차감 확인
      if (window.confirm(`이 전장에 입성하시겠습니까?\n최초 1회 3,000P가 차감됩니다. (잔여: ${user?.points?.toLocaleString()}P)`)) {
        try {
          const token = localStorage.getItem('token');
          // 백엔드의 join API 호출
          const res = await axios.post(`https://the-pivot-logical-battleground.onrender.com/api/topics/${topic._id}/join`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });

          // 3. 리덕스 상태 업데이트 (차감된 포인트와 새 참여 목록 반영)
          dispatch(updatePoints(res.data)); 
          dispatch(setCurrentTopic(topic));
          
          alert("성공적으로 입성했습니다. 전장에 승리를 가져오십시오.");
          navigate(`/topics/${topic._id}`);
        } catch (err) {
          // 포인트 부족 등 에러 처리
          alert(err.response?.data?.message || "입성에 실패했습니다.");
        }
      }
    }
  };

  return (
    <div style={styles.container}>

      {showTutorial && <TutorialModal onClose={closeTutorial} />}

      <div style={styles.topicListContainer}>
        {/* 히어로 섹션 */}
        <div style={styles.heroSection}>
          <h1 style={styles.heroTitle}>전장 목록</h1>
          <p style={styles.heroSub}>논리가 무기가 되고, 팩트가 방패가 되는 곳.</p>
        
          {/* 🚩 [추가] 선택형 도움말 버튼: 구석에 배치하여 아는 사람만 보게 합니다. */}
          <button 
            onClick={() => setShowTutorial(true)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'transparent', border: '1px solid #444',
              borderRadius: '50%', width: '30px', height: '30px',
              color: '#888', cursor: 'pointer', fontSize: '1rem'
            }}
            title="이용 방법 확인"
          >
            ?
          </button>
        </div>

        {/* 관리자 버튼 */}
        {isAdmin && (
          <div style={styles.adminActionRow}>
            <button 
              style={styles.createTopicBtn}
              onClick={() => navigate('/topics/new')}
            >
              + 새로운 전장 선포
            </button>
          </div>
        )}

        {/* 전장 목록 그리드 */}
        <div style={styles.topicGrid}>
          {memoizedTopics.map(topic => (
            <div 
              key={topic._id} 
              onMouseEnter={() => setHoveredId(topic._id)}
              onMouseLeave={() => setHoveredId(null)}
              onClick={() => handleEntry(topic)}
              style={{
                ...styles.topicCard,
                ...(hoveredId === topic._id ? styles.topicCardHover : {})
              }}
            >

              {/* 1. 제목 필터링: 첫 줄만 노출 */}
              <h3 style={styles.topicTitle}>
                {topic.mainTitle}
              </h3>

              {/* 2. 실시간 화력 지수 (글 + 댓글 수) */}
              <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.8rem', color: '#888' }}>현재 화력:</span>
                <span style={{ fontSize: '0.9rem', color: '#00d4ff', fontWeight: 'bold' }}>
                  🔥 {topic.totalActivity?.toLocaleString() || 0} 개의 논리
                </span>
              </div>

              {/* 3. 미니 배틀 게이지 (실시간 수치 반영) */}
              <div style={{ width: '100%', height: '6px', background: '#222', borderRadius: '3px', overflow: 'hidden', display: 'flex', marginBottom: '15px' }}>
                <div style={{ width: `${topic.currentGauge?.blue ?? 50}%`, background: '#00d4ff', transition: 'width 0.5s' }} />
                <div style={{ width: `${topic.currentGauge?.red ?? 50}%`, background: '#ff4e50', transition: 'width 0.5s' }} />
              </div>

              {/* 장식용 네온 바 */}
              <div style={{
                position: 'absolute', top: 0, left: 0, width: '4px', height: '100%',
                background: 'linear-gradient(to bottom, #00d4ff, #ff4e50)'
              }}></div>

              {/* <h3 style={styles.topicTitle}>{topic.title}</h3> */}
              <p style={{ color: '#666', fontSize: '0.9rem', lineHeight: '1.4' }}>
                현재 수많은 논객들이 전장에서 대립하고 있습니다. 진영을 선택하고 논리를 펼치십시오.
              </p>
              
              <div style={styles.topicFooter}>
                <span style={{ color: '#444', fontSize: '0.8rem', fontWeight: 'bold' }}>THE PIVOT BATTLEGROUND</span>
                <button style={styles.enterBtn}>전장 입장</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Topics;